import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme } from '../../../theme';
import { fs, iconSize, s, v } from '../../../utils/libs';

interface SheetHeaderProps {
  title: string;
  /** Shows the round × on the right. */
  onClose?: () => void;
  /** Shows ‹ before the title, for a sheet opened from another sheet. */
  onBack?: () => void;
  /** Short status text before the ×, e.g. "2 selected". */
  note?: string;
}

/**
 * Title row for bottom sheets and side panels: title on the left, round ×
 * (or ‹ back) to dismiss, hairline underneath. Pass it as a sheet's `header`.
 */
const SheetHeader: React.FC<SheetHeaderProps> = ({
  title,
  onClose,
  onBack,
  note,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={styles.header}>
      <View style={styles.left}>
        {onBack ? (
          <Pressable
            onPress={onBack}
            accessibilityRole="button"
            accessibilityLabel="Back"
            style={styles.backButton}>
            <MaterialCommunityIcons
              name="chevron-left"
              size={iconSize(26)}
              color={theme.colors.text}
            />
          </Pressable>
        ) : null}
        <Text style={styles.title} accessibilityRole="header">
          {title}
        </Text>
      </View>
      {note ? <Text style={styles.note}>{note}</Text> : null}
      {onClose ? (
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close"
          style={({ pressed }) => [
            styles.closeButton,
            pressed && styles.pressed,
          ]}>
          <MaterialCommunityIcons
            name="close"
            size={iconSize(20)}
            color={theme.colors.text}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const pointer = Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {};

const createStyles = (theme: any) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(8),
      minHeight: v(56),
      paddingLeft: s(20),
      paddingRight: s(12),
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    left: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
    },
    title: {
      flexShrink: 1,
      fontFamily: GTFonts.bold,
      fontSize: fs(18),
      lineHeight: fs(24),
      color: theme.colors.text,
    },
    note: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
      paddingRight: s(8),
    },
    closeButton: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.surfaceSecondary,
      ...pointer,
    },
    backButton: {
      width: s(40),
      height: s(44),
      marginLeft: -s(12),
      alignItems: 'center',
      justifyContent: 'center',
      ...pointer,
    },
    pressed: {
      opacity: 0.8,
    },
  });

export default SheetHeader;
